import { Controller } from 'stimulus'

/**
 * Manages js for the full income forms of the income steps (StimulusJS Controller)
 *
 * @class FullIncomeController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'error',
    'form',
    'incomeType',
    'id',
    'input',
    'startDate'
  ]

  /**
   * @function Controllers.FullIncomeController.maybeTriggerASubmit
   * @memberof Controllers
   * @description Triggering event is a mm/yyyy date which is complete at 7 chars.
   * This function delays submision until all 7 chars are present.
   *
   * @param {Event} event
   *
   * @return {undefined}
   *
   */
  maybeTriggerASubmit(event) {
    if (event.target.value.length === 7) {
      this.triggerASubmit(event)
    }
  }

  /**
   * @function Controllers.FullIncomeController.maybeTriggerAnEditSubmit
   * @memberof Controllers
   * @description After a successful submit the form will have an idTarget.value
   * and fields with this action will have any edits detected and submitted
   *
   * @param {Event} event
   *
   * @return {undefined}
   *
   */
  maybeTriggerAnEditSubmit(event) {
    clearTimeout(this.timeoutEdit)
    this.timeoutEdit = setTimeout(() => {
      this.idTarget.value ? this.triggerASubmit(event) : null
    }, 500)
  }

  /**
  * @function Controllers.FullIncomeController.triggerASubmit
  * @memberof Controllers
  * @description Determine if a submission should be a create (POST) or an update
  * (PATCH). Based on that, send nextVisible and/or spawnNew custom events to
  * borrower-income-controller
  *
  * @param {Event} event
  *
  * @return {undefined}
  *
  */
  async triggerASubmit(event) {
    clearTimeout(this.timeoutSubmit)
    this.timeoutSubmit = setTimeout(async () => {
      if (this.idTarget.value) {
        if (await this._updateIncome()) {
          this.formTarget.dispatchEvent(new CustomEvent('nextVisible', { bubbles: true, detail: { toggle: true } }))
        }
      } else if (await this._createIncome()) {
        this.formTarget.dispatchEvent(new CustomEvent('spawnNew', { bubbles: true,
          detail:  { type: this.incomeTypeTarget.value, start_on: this.startDateTarget.value } }))
        this.formTarget.dispatchEvent(new CustomEvent('nextVisible', { bubbles: true, detail: { toggle: true } }))
      }
    }, 500)
  }

  /**
  * @function Controllers.FullIncomeController.deleteIncome
  * @memberof Controllers
  * @description Delete the DOM form. Potentially send a DELETE request if there
  * is an associated Income.
  *
  * @return {undefined} manipulates the DOM
  *
  */
  async deleteIncome() {
    if (this.idTarget.value) {
      await fetch(`/v1/borrower_incomes/${this.idTarget.value}`, {
        method:      'DELETE',
        credentials: 'same-origin',
        headers:     { 'content-type': 'application/json' },
        mode:        'cors'
      })
    }

    this.formTarget.remove()
  }

  /**
   * @function Controllers.FullIncomeController._submitIncome
   * @memberof Controllers
   * @description Submits a create (POST) request to the borrower_incomes_controller.rb,
   * then sets the returned id to the hidden id field if create request is successful.
   * Sends the request result to the _maybeThrowErrors function.
   * @private
   *
   * @return {Boolean} based on whether there are errors
   *
   */
  async _createIncome() {
    const data = this._dataFromIncome()
    const result = await fetch('/v1/borrower_incomes', {
      method:      'POST',
      credentials: 'same-origin',
      headers:     { 'content-type': 'application/json' },
      mode:        'cors',
      body:        JSON.stringify(data)
    })

    if (result.status === 201) {
      const newIncome = await result.json()
      this.idTarget.value = newIncome['id']
      this.inputTargets.forEach(input => {
        input.id = input.id + '_' + newIncome['id']
      })
    }

    return this._maybeThrowErrors(result)
  }

  /**
   * @function Controllers.FullIncomeController._updateIncome
   * @memberof Controllers
   * @description Submits an update (PATCH) request to the borrower_incomes_controller.
   * Sends the request result to the _maybeThrowErrors function.
   * @private
   *
   * @return {Boolean} based on whether there are errors
   *
   */
  async _updateIncome() {
    const data = this._dataFromIncome()
    const result = await fetch(`/v1/borrower_incomes/${this.idTarget.value}`, {
      method:      'PATCH',
      credentials: 'same-origin',
      headers:     { 'content-type': 'application/json' },
      mode:        'cors',
      body:        JSON.stringify(data)
    })

    return this._maybeThrowErrors(result)
  }

  /**
   * @function Controllers.FullIncomeController._maybeThrowErrors
   * @memberof Controllers
   * @description If the result is a 422, errorFields are remove and then new
   * errors are placed on the corresponding DOM inputs. If there are erros,
   * customEvent nextVisible with a false value is sent to borrower-income-controller.js
   * @private
   *
   * @param {Result} result
   *
   * @return {Boolean} based on whether there are errors
   *
   */
  async _maybeThrowErrors(result) {
    if (result.status === 422) {
      const json = await result.json()
      this._removeErrorFields()
      this._addErrorFields(json.errors)
      this.formTarget.dispatchEvent(new CustomEvent('nextVisible', { bubbles: true, detail: { toggle: false } }))
      return false
    } else {
      this._removeErrorFields()
      return true
    }
  }

  /**
   * @function Controllers.FullIncomeController._dataFromIncome
   * @memberof Controllers
   * @description Creates an object of data from the form targets
   * @private
   *
   * @return {object} an income object ready to be JSON.stringified
   *
   */
  _dataFromIncome() {
    const inputs = this.inputTargets
    const income = {}
    inputs.map(input => {
      income[input.dataset.fieldName] = input.value
    })

    income['borrower_id'] = this.data.get('borrower')
    income['income_type'] = this.incomeTypeTarget.value
    income['start_on'] = income['start_on'].length === 7 ? this._formatDates(income['start_on']) : null
    income['end_on'] = income['end_on'] ? this._formatDates(income['end_on']) : ''
    return income
  }

  /**
   * @function Controllers.FullIncomeController._formatDates
   * @memberof Controllers
   * @description Format dates for use in the backend
   * @private
   *
   * @param {Date} date
   *
   * @return {String}
   *
   */
  _formatDates(date) {
    const [ month, year ] = date.split('/')
    return `${year}-${month}-01`
  }

  /**
  * @function Controllers.FullIncomeController._addErrorFields
  * @memberof Controllers
  * @description Add errors to their respective input fields by dispatching a
  * spawnErrors custom event to the borrower-income-controller.js
  * Add a triggerASubmit action to those fields
  * @private
  *
  * @param {Errors} errors
  *
  */
  _addErrorFields(errors) {
    this.inputTargets.forEach(input => {
      const fieldName = input.dataset.fieldName
      if (errors[fieldName]) {
        input.dispatchEvent(new CustomEvent('spawnErrors', { detail: errors[fieldName] }))
        input.setAttribute('data-action', 'input->full-income#triggerASubmit')
      }
    })
  }

  /**
  * @function Controllers.FullIncomeController._removeErrorFields
  * @memberof Controllers
  * @description Remove errors from input fields
  * @private
  *
  * @return {undefined} manipulates the DOM
  *
  */
  _removeErrorFields() {
    const errorElements = this.errorTargets
    errorElements.forEach(errorField => {
      const input = errorField.parentElement.querySelector('input')
      input.dispatchEvent(new CustomEvent('removeErrors'))
    })
  }
}
